import VariantPicker from "@theme/variant-picker";
import { Component } from "@theme/component";
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";

const CALLOUT_GRID_SELECTOR = [
  ".product-grid.layout-panel-grid",
  ".featured-collection__inner.layout-panel-grid",
  ".featured-collection-block__inner.layout-panel-grid",
  ".product-recommendations__inner.layout-panel-grid",
  ".section--featured-collection .swiper-wrapper.layout-panel-grid-mobile",
  ".product-recommendations .swiper-wrapper.layout-panel-grid-mobile",
].join(",");

let calloutEqualizeFrame = null;
/** @type {ResizeObserver | null} */
let calloutResizeObserver = null;
/** @type {Set<Element>} */
const observedCalloutGrids = new Set();
/** @type {Set<Element>} */
const observedCalloutTargets = new Set();

function observeCalloutTarget(target) {
  if (!(target instanceof Element) || observedCalloutTargets.has(target)) return;

  if (!calloutResizeObserver) {
    calloutResizeObserver = new ResizeObserver(() => {
      scheduleCalloutHeightEqualize();
    });
  }

  observedCalloutTargets.add(target);
  calloutResizeObserver.observe(target);
}

function scheduleCalloutHeightEqualize(root = document) {
  if (calloutEqualizeFrame) cancelAnimationFrame(calloutEqualizeFrame);

  calloutEqualizeFrame = requestAnimationFrame(() => {
    calloutEqualizeFrame = null;
    equalizeCalloutHeights(root);
    clampCalloutDescriptions(root);
  });
}

const MOBILE_CALLOUT_MQL = window.matchMedia("(max-width: 767.98px)");

function resetCalloutDescriptionClamp(description) {
  if (!(description instanceof HTMLElement)) return;

  description.style.removeProperty("-webkit-line-clamp");
  description.style.removeProperty("display");
  description.style.removeProperty("overflow");
  description.style.removeProperty("-webkit-box-orient");
}

function clampCalloutDescriptions(root = document) {
  const callouts = [];

  if (root instanceof Element) {
    if (root.matches?.(".product-card--callout")) {
      callouts.push(root);
    }
    root.querySelectorAll?.(".product-card--callout").forEach((callout) => callouts.push(callout));
  } else {
    document.querySelectorAll(".product-card--callout").forEach((callout) => callouts.push(callout));
  }

  callouts.forEach((card) => {
    const description = card.querySelector(".product-card__callout-description");
    if (!(description instanceof HTMLElement)) return;

    if (!MOBILE_CALLOUT_MQL.matches) {
      resetCalloutDescriptionClamp(description);
      return;
    }

    const overlay = card.querySelector(".product-card__callout");
    const action = card.querySelector(".product-card__callout-action");

    if (!(overlay instanceof HTMLElement)) return;

    resetCalloutDescriptionClamp(description);

    const overlayStyles = getComputedStyle(overlay);
    const paddingBlock =
      parseFloat(overlayStyles.paddingTop) + parseFloat(overlayStyles.paddingBottom);
    const gap = parseFloat(overlayStyles.rowGap) || 0;
    const actionHeight = action instanceof HTMLElement ? action.offsetHeight : 0;
    const availableHeight = overlay.clientHeight - paddingBlock - actionHeight - (actionHeight ? gap : 0);

    if (availableHeight <= 0) return;

    const lineHeight = parseFloat(getComputedStyle(description).lineHeight);
    if (!lineHeight) return;

    const lines = Math.max(1, Math.floor(availableHeight / lineHeight));

    description.style.display = "-webkit-box";
    description.style.overflow = "hidden";
    description.style.webkitBoxOrient = "vertical";
    description.style.setProperty("-webkit-line-clamp", String(lines));
  });
}

function equalizeCalloutHeights(root = document) {
  /** @type {Set<Element>} */
  const grids = new Set();

  root.querySelectorAll?.(".product-card--callout").forEach((callout) => {
    const grid = callout.closest(CALLOUT_GRID_SELECTOR);
    if (grid) grids.add(grid);
  });

  if (root instanceof Element && root.matches?.(CALLOUT_GRID_SELECTOR) && root.querySelector(".product-card--callout")) {
    grids.add(root);
  }

  grids.forEach((grid) => {
    const standardCards = grid.querySelectorAll("product-card:not(.product-card--callout)");
    const callouts = grid.querySelectorAll("product-card.product-card--callout");

    let maxHeight = 0;
    standardCards.forEach((card) => {
      maxHeight = Math.max(maxHeight, Math.ceil(card.getBoundingClientRect().height));
    });

    callouts.forEach((callout) => {
      if (maxHeight > 0) {
        callout.style.minHeight = `${maxHeight}px`;
        callout.classList.add("product-card--callout-sized");
      } else {
        callout.style.removeProperty("min-height");
        callout.classList.remove("product-card--callout-sized");
      }
    });
  });
}

function observeCalloutGrid(grid) {
  if (!(grid instanceof Element)) return;

  if (!observedCalloutGrids.has(grid)) {
    observedCalloutGrids.add(grid);
    observeCalloutTarget(grid);
  }

  grid.querySelectorAll("product-card").forEach((card) => {
    observeCalloutTarget(card);
  });
}

function initCalloutHeightEqualize(context = document) {
  context.querySelectorAll?.(CALLOUT_GRID_SELECTOR).forEach((grid) => {
    if (grid.querySelector(".product-card--callout")) {
      observeCalloutGrid(grid);
    }
  });

  scheduleCalloutHeightEqualize(context);
}

export class ProductCard extends Component {
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );
    return checkedInput?.dataset.variantId || null;
  }

  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);

    if (this.classList.contains("product-card--callout")) {
      const grid = this.closest(CALLOUT_GRID_SELECTOR);
      if (grid) observeCalloutGrid(grid);
      scheduleCalloutHeightEqualize(this);
      observeCalloutTarget(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.classList.contains("product-card--callout")) {
      scheduleCalloutHeightEqualize();
    }
  }

  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
    requestAnimationFrame(() => {
      this.#updateVariantImages();
    });
  };

  #updateProductUrl(event) {
    // Combined listing: update from newProduct data (works with cache)
    if (event.detail.data.newProduct?.url) {
      const productUrl = event.detail.data.newProduct.url;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink) productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;

      const featuredMediaUrl = event.detail.resource?.featured_media?.preview_image?.src;
      if (featuredMediaUrl && this.closest("product-card-link")) {
        this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
      }
      return;
    }

    // Fallback: get URL from HTML (cache miss)
    if (!event.detail.data.html) return;

    const anchorElement = event.detail.data.html.querySelector("product-card a");
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector("product-card-link")
      ?.getAttribute("data-featured-media-url");

    if (featuredMediaUrl && this.closest("product-card-link")) {
      this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute("href")?.trim() === "") return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      if (productCardLink) productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) cardGalleryLink.href = productUrl;
      if (productTitleLink instanceof HTMLAnchorElement) productTitleLink.href = productUrl;
    }
  }

  #updateVariantImages(event) {
    const mainImage = this.refs.cardGalleryLink?.querySelector(".product-card-main-image");
    if (!mainImage) return;

    const selectedSwatch = this.querySelector('input[type="radio"]:checked');
    if (!selectedSwatch) return;

    // Combined listing: check product match
    const swatchProductId = this.variantPicker?.dataset.productId;
    const currentProductId = this.dataset.productId || this.closest("product-card")?.dataset.productId;
    if (swatchProductId && currentProductId && swatchProductId !== currentProductId) {
      return;
    }

    const selectedLabel = selectedSwatch.closest("label");
    if (!selectedLabel) return;

    // Skip if swatch is hidden
    const swatchItem = selectedLabel.closest(".variant-option__swatch");
    if (swatchItem?.classList.contains("hidden")) return;

    const selectedImage = selectedLabel.querySelector(".media__image");

    // Unavailable variant: if swatch doesn't match event variant, try HTML first
    const swatchVariantId = selectedSwatch.dataset.variantId;
    const eventVariantId = event?.detail.resource?.id;
    if (eventVariantId && swatchVariantId && swatchVariantId !== eventVariantId && event?.detail.data.html) {
      if (this.#updateImageFromHTML(mainImage, event.detail.data.html)) {
        return;
      }
    }

    // Wait for image if not found yet
    if (!selectedImage) {
      const mediaId = selectedLabel.dataset.mediaId || selectedSwatch.dataset.optionMediaId;
      if (mediaId) {
        this.#waitForImageAndUpdate(mainImage, selectedLabel);
      }
      return;
    }

    // Apply image immediately if src exists
    if (selectedImage.src || selectedImage.currentSrc) {
      this.#applyImageToMain(mainImage, selectedImage);
      if (!selectedImage.complete) {
        selectedImage.addEventListener(
          "load",
          () => {
            if (selectedImage.srcset && mainImage.srcset !== selectedImage.srcset) {
              mainImage.srcset = selectedImage.srcset;
            }
          },
          { once: true }
        );
      }
    } else {
      selectedImage.addEventListener(
        "load",
        () => {
          this.#applyImageToMain(mainImage, selectedImage);
        },
        { once: true }
      );
    }
  }

  #waitForImageAndUpdate(mainImage, label) {
    const checkImage = () => {
      const img = label.querySelector(".media__image");
      if (img && (img.complete || img.naturalWidth > 0)) {
        this.#applyImageToMain(mainImage, img);
        return true;
      }
      return false;
    };

    if (checkImage()) return;

    requestAnimationFrame(() => {
      if (checkImage()) return;
      const img = label.querySelector(".media__image");
      if (img) {
        img.addEventListener("load", () => this.#applyImageToMain(mainImage, img), { once: true });
      }
    });
  }

  #updateImageFromHTML(mainImage, html) {
    const newCard = html.querySelector(`product-card[data-product-id="${this.dataset.productId}"]`);
    if (!newCard) return false;

    const newMainImage = newCard.querySelector(".product-card-main-image");
    if (!newMainImage) return false;

    let updated = false;
    if (newMainImage.srcset && mainImage.srcset !== newMainImage.srcset) {
      mainImage.srcset = newMainImage.srcset;
      updated = true;
    }
    if (newMainImage.src && mainImage.src !== newMainImage.src) {
      mainImage.src = newMainImage.src;
      updated = true;
    }
    return updated;
  }

  #applyImageToMain(mainImage, sourceImage) {
    if (!mainImage || !sourceImage) return;

    const newSrcset = sourceImage.srcset || "";
    const newSrc = sourceImage.currentSrc || sourceImage.src;

    if (newSrcset && mainImage.srcset !== newSrcset) {
      mainImage.srcset = newSrcset;
    }
    if (newSrc && mainImage.src !== newSrc) {
      mainImage.src = newSrc;
    }
  }

  #updatePreOrderMessage(event) {
    const container = this.querySelector("[data-product-card-pre-order-container]");
    if (!(container instanceof HTMLElement)) return;

    const isAvailable = event.detail.resource?.available ?? false;
    const newProduct = event.detail.data.newProduct;

    if (!newProduct) {
      const message = container.querySelector("[data-pre-order-message]");
      if (message instanceof HTMLElement) message.hidden = !isAvailable;
      return;
    }

    const html = event.detail.data.html;
    if (!html) {
      container.replaceChildren();
      return;
    }

    const stateTemplate = html.querySelector(
      `template[data-product-form-state][data-product-id="${CSS.escape(String(newProduct.id))}"]`
    );
    const sourceMessage =
      stateTemplate instanceof HTMLTemplateElement
        ? stateTemplate.content.querySelector("[data-pre-order-message]")
        : null;

    container.replaceChildren();
    if (sourceMessage) {
      const message = sourceMessage.cloneNode(true);
      if (message instanceof HTMLElement) message.hidden = !isAvailable;
      container.appendChild(message);
    }
  }

  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.#updateProductUrl(event);
    this.#updatePreOrderMessage(event);

    if (event.target !== this.variantPicker && event.detail.data.html) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    // Restore swatches expanded state after morph
    if (this.variantPicker instanceof SwatchesVariantPicker && event.detail.data.html) {
      const expandedKey = this.variantPicker.getStorageKey();
      if (sessionStorage.getItem(expandedKey) === "true") {
        requestAnimationFrame(() => {
          this.variantPicker.restoreExpandedState();
        });
      }
    }

    // Update images: immediate for cache, deferred for cache miss
    if (event.detail.data.isOptimistic || event.detail.data.isBackgroundSync) {
      this.#updateVariantImages(event);
    } else {
      requestAnimationFrame(() => {
        this.#updateVariantImages(event);
      });
    }
  };

  get variantPicker() {
    return this.querySelector("swatches-variant-picker");
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCard);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initCalloutHeightEqualize(), { once: true });
} else {
  initCalloutHeightEqualize();
}

window.addEventListener("load", () => scheduleCalloutHeightEqualize(), { passive: true });

MOBILE_CALLOUT_MQL.addEventListener("change", () => scheduleCalloutHeightEqualize());

class SwatchesVariantPicker extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest("product-card");

    // Clear storage on page load (not on morph) - detect by checking if component is new
    if (!this.dataset.initialized) {
      const expandedKey = this.getStorageKey();
      sessionStorage.removeItem(expandedKey);
      this.dataset.initialized = "true";
    }
  }

  updatedCallback() {
    super.updatedCallback?.();

    // Restore expanded state after component update (morph)
    const expandedKey = this.getStorageKey();
    const shouldExpand = sessionStorage.getItem(expandedKey) === "true";

    if (shouldExpand) {
      this.restoreExpandedState();
    }
  }

  getStorageKey() {
    const productId = this.dataset.productId || "unknown";
    return `swatches-expanded-${productId}`;
  }

  restoreExpandedState() {
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    const showMoreButton = this.querySelector(".variant-option__swatch--show-more");

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    if (showMoreButton) showMoreButton.classList.add("hidden");
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes("-swatch");
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || "0");
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // Unavailable variant with alternatives: load available variant instead
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      event.stopPropagation();
      this.updateSelectedOption(clickedSwatch);

      const productUrl = this.dataset.productUrl?.split("?")[0];
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set("variant", firstAvailableVariantId);
      url.searchParams.set("section_id", "section-rendering-product-card");

      const fetchToken = this.beginVariantFetch();
      this.fetchUpdatedSection(url.href, false, false, fetchToken);
      return;
    }

    super.variantChanged(event);
  }

  showAllSwatches(event) {
    event?.preventDefault();
    const hiddenSwatches = this.querySelectorAll(".variant-option__swatch.hidden");
    if (hiddenSwatches.length === 0) return;

    hiddenSwatches.forEach((swatch) => swatch.classList.remove("hidden"));
    this.querySelector(".variant-option__swatch--show-more")?.classList.add("hidden");
    sessionStorage.setItem(this.getStorageKey(), "true");
  }
}
if (!customElements.get("swatches-variant-picker")) {
  customElements.define("swatches-variant-picker", SwatchesVariantPicker);
}
